/**
 * The real Cohesive design-system stylesheets, imported as raw text and
 * reduced to what a browser can use inside an iframe `srcDoc`.
 *
 * Opaque-origin block iframes can't inherit the app's CSS custom properties,
 * so the token vocabulary must ship inside the document. Instead of a
 * hand-ported copy (the previous approach — a "keep in sync" comment away
 * from drifting), this module derives the injectable stylesheet from
 * `src/styles/cohesive-theme.css` + `src/styles/cohesive-tokens.css` at
 * module load. Token values now have exactly one source of truth.
 *
 * Derivation is a line/block filter, NOT a CSS transformer:
 * - `@import` lines are dropped — they would resolve (and 404) against
 *   `about:srcdoc`. The imported files are concatenated explicitly instead
 *   (`cohesive-palette-theme.css` is deliberately excluded: it is 100%
 *   Tailwind `@theme inline` registrations, dead weight in a browser).
 * - `@theme` / `@theme inline` blocks are stripped — they are Tailwind v4
 *   build-time constructs. A browser would ignore them anyway (unknown
 *   at-rules are skipped per spec), but they are ~6 KB of dead text per
 *   srcDoc. NOTE: tokens defined ONLY inside `@theme` blocks do not exist at
 *   browser runtime; everything agent blocks are taught to use must live in a
 *   real `:root` / `.dark` rule (locked by htmlIframeSrcDoc.test.ts).
 * - Comments and blank lines are dropped (payload).
 * - `@layer base` is KEPT: unlayered agent CSS beats layered tokens by
 *   cascade-layer rules, which is exactly the "agent CSS can override the
 *   injected tokens" contract the head-prepend order used to approximate.
 *
 * Dark mode: the stylesheet's own `.dark` class selector is kept, and
 * `prepareSrcDocHtml` sets `class="dark"` on the iframe's `<html>` when
 * `colorScheme: "dark"` — mirroring how the app itself flips themes.
 *
 * Packaging: ui-components builds with plain `tsc`, which passes the `?raw`
 * specifier through untouched; the build script copies `src/styles/*.css`
 * into `dist/styles/` so the relative import resolves for dist consumers
 * (same mechanism as `htmlBlockRuntime.script.js`).
 */

import cohesiveThemeCssRaw from "../styles/cohesive-theme.css?raw";
import cohesiveTokensCssRaw from "../styles/cohesive-tokens.css?raw";
import { stripForIframeInjection } from "./blockTokensAssemble";

// Re-export the raw stylesheet text so cohesiveBlockTokens.ts can assemble
// from the same inputs without duplicating the `?raw` imports.
export { cohesiveThemeCssRaw, cohesiveTokensCssRaw };

/**
 * The injectable Cohesive theme stylesheet: `cohesive-tokens.css` (elevation,
 * hairlines) + `cohesive-theme.css` (identity palette, n-ladder, spectrum,
 * lifecycle, sticky, surfaces, shadcn channels), in their `@import` order,
 * with both `:root` and `.dark` blocks intact. Theme selection happens via a
 * `dark` class on the iframe's root element, not by picking a variant here.
 */
export const COHESIVE_IFRAME_THEME_CSS: string = [
  stripForIframeInjection(cohesiveTokensCssRaw),
  stripForIframeInjection(cohesiveThemeCssRaw),
].join("\n");
